import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Activity, Camera, CheckCircle2, Download, Loader2, RefreshCw, Trash2, Undo2, Wand2, Wifi, WifiOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { NativeSelect } from '@/components/ui/native-select';
import { ernaehrungAPI } from '@/lib/api';
import { fehlertext, MitgliedSuche } from '@/pages/ErnaehrungPage';
import { Scanner } from '@/components/CheckupStepper';
import { useAuth } from '@/contexts/AuthContext';

/* Waagenstation (29.10.2026, backend/ernaehrung/waagen_station.py): Gastmessungen der InBody 270 kommen über den
   Raspberry Pi hier an und werden per Tipp einem Mitglied gegeben. Gespeichert wird erst beim Zuordnen — mit
   denselben Regeln wie die Handeingabe am Mitglied (Schalter, ab 18, Einwilligung oder Papier-Haken). Gastmessungen
   speichert die Waage nicht: „Waage scannen“ liest den QR-Code vom Display mit der iPad-Kamera (belegtes Format,
   Zeitfenster wie in der App) und legt die Messung in denselben Eingang. Automatik (Schalter, nur Leitung):
   eindeutige Messungen ordnet der Server selbst zu, 24 h rückgängig; Export = CSV aller Messungen (Leitung). Alle Sätze,
   Werte und Vorschläge kommen fertig vom Server; nach 24 Stunden ist eine nicht zugeordnete Messung weg. */

const zeit = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' }) : '–');
const BEDINGUNG = [{ value: 'unbekannt', label: 'nicht gefragt' }, { value: 'nein', label: 'nein' }, { value: 'ja', label: 'ja' }];
const STATUS = { zugeordnet: 'zugeordnet', verworfen: 'verworfen', schon_erfasst: 'schon per QR-Code erfasst' };

function Station({ s }) {
  const farbe = !s.online ? 'text-red-700' : s.waage_verbunden ? 'text-emerald-700' : 'text-amber-700';
  const Icon = s.online ? Wifi : WifiOff;
  return (
    <p className={`text-sm flex items-center gap-2 ${farbe}`}>
      <Icon className="w-4 h-4" aria-hidden="true" />
      {s.station_id}: {!s.online ? 'nicht erreichbar' : s.waage_verbunden ? 'bereit' : 'online, Waage nicht verbunden'}
      <span className="text-muted-foreground">· zuletzt {zeit(s.letzter_ping)}{s.warteschlange ? ` · ${s.warteschlange} wartet auf Versand` : ''}</span>
    </p>
  );
}

function Messung({ m, onFertig }) {
  const [person, setPerson] = useState(null);
  const [papier, setPapier] = useState(false);
  const [bed, setBed] = useState({ trainiert_12h: 'unbekannt', gegessen_3h: 'unbekannt' });
  const [laeuft, setLaeuft] = useState(false);
  const [rueckfrage, setRueckfrage] = useState(null);

  const zuordnen = async () => {
    if (!person) return;
    setLaeuft(true); setRueckfrage(null);
    try {
      const r = await ernaehrungAPI.stationZuordnen(m.id, { lead_id: person.lead_id || person.id, bedingungen: bed, einwilligung_papier: papier });
      toast.success(r.data.bekannt ? `War schon bei ${person.name} gespeichert` : `Messung bei ${person.name} gespeichert`);
      onFertig();
    } catch (e) {
      if (e?.response?.data?.zustand === 'einwilligung_noetig') setRueckfrage(fehlertext(e));
      else toast.error(fehlertext(e));
    } finally { setLaeuft(false); }
  };
  const verwerfen = async () => {
    setLaeuft(true);
    try { await ernaehrungAPI.stationVerwerfen(m.id); toast.success('Verworfen'); onFertig(); }
    catch (e) { toast.error(fehlertext(e)); } finally { setLaeuft(false); }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex flex-wrap items-center gap-2">
          <Activity className="w-4 h-4" aria-hidden="true" /> Gastmessung {m.titel}
          {m.angaben && <Badge variant="outline">{m.angaben.groesse_cm} cm · {m.angaben.alter} J · {m.angaben.geschlecht === 'w' ? 'weiblich' : 'männlich'}</Badge>}
          <span className="text-xs font-normal text-muted-foreground">verfällt {zeit(m.laeuft_ab_am)}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!m.lesbar && <p className="text-sm text-amber-700">Nicht lesbar ({m.grund}). Die Station arbeitet, das Format der Waage wird aber noch nicht gelesen – Werte bitte am Mitglied von Hand eintragen.</p>}
        {m.lesbar && (
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-sm">
            {m.zeilen.map((z) => (<div key={z.name}><dt className="text-muted-foreground text-xs">{z.name}</dt><dd>{z.wert} {z.einheit}</dd></div>))}
          </dl>
        )}
        {m.lesbar && m.automatik_grund && <p className="text-xs text-muted-foreground">Automatik: {m.automatik_grund}</p>}
        {m.lesbar && (
          <div className="space-y-2">
            {m.vorschlaege.length > 0 && (
              <div className="flex flex-wrap gap-2" role="group" aria-label="Vorschläge">
                {m.vorschlaege.map((v) => (
                  <Button key={v.lead_id} type="button" size="sm" variant={person?.lead_id === v.lead_id ? 'default' : 'outline'} onClick={() => setPerson(v)}>
                    {v.name}<span className="ml-1 text-xs opacity-70">({v.gruende.join(', ')})</span>
                  </Button>
                ))}
              </div>
            )}
            <MitgliedSuche onWahl={(t) => setPerson({ lead_id: t.id, name: t.name })} />
            {person && (
              <div className="rounded-md border p-3 space-y-2">
                <p className="text-sm">Zuordnen zu <strong>{person.name}</strong></p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {[['trainiert_12h', 'In den letzten 12 Stunden trainiert?'], ['gegessen_3h', 'In den letzten 3 Stunden gegessen?']].map(([k, label]) => (
                    <label key={k} className="text-sm space-y-1 block">{label}
                      <NativeSelect value={bed[k]} onChange={(v) => setBed({ ...bed, [k]: v })} options={BEDINGUNG} />
                    </label>
                  ))}
                </div>
                {rueckfrage && <p className="text-sm text-amber-700">{rueckfrage}</p>}
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={papier} onChange={(e) => setPapier(e.target.checked)} />
                  Einwilligung liegt auf Papier vor (nur ankreuzen, wenn das stimmt)
                </label>
                <Button type="button" onClick={zuordnen} disabled={laeuft}>
                  {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Speichern bei {person.name}
                </Button>
              </div>
            )}
          </div>
        )}
        <Button type="button" variant="ghost" size="sm" onClick={verwerfen} disabled={laeuft} className="text-muted-foreground">
          <Trash2 className="w-4 h-4" /> Verwerfen
        </Button>
      </CardContent>
    </Card>
  );
}

const LEITUNG = ['admin', 'manager'];
const tag = (d) => d.toISOString().slice(0, 10);

function Export() {
  const [von, setVon] = useState(tag(new Date(Date.now() - 365 * 864e5)));
  const [bis, setBis] = useState(tag(new Date()));
  const [laeuft, setLaeuft] = useState(false);
  const holen = async () => {
    setLaeuft(true);
    try {
      const r = await ernaehrungAPI.stationExport(von, bis);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `koerperanalyse_${von}_${bis}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error('Export fehlgeschlagen'); } finally { setLaeuft(false); }
  };
  return (
    <Card><CardContent className="pt-4 flex flex-wrap items-end gap-2">
      <div className="text-sm mr-auto"><p className="font-medium">Alle Messungen exportieren</p>
        <p className="text-muted-foreground text-xs">CSV für Excel – jede Messung mit Mitglied und Werten (Bestenlisten, Verläufe). Gesundheitsdaten: nur auf Studio-Geräten speichern.</p></div>
      <label className="text-xs">von <input type="date" value={von} onChange={(e) => setVon(e.target.value)} className="border rounded px-2 py-1 bg-background" /></label>
      <label className="text-xs">bis <input type="date" value={bis} onChange={(e) => setBis(e.target.value)} className="border rounded px-2 py-1 bg-background" /></label>
      <Button type="button" size="sm" variant="outline" onClick={holen} disabled={laeuft}>{laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} CSV</Button>
    </CardContent></Card>
  );
}

export default function WaagePage() {
  const { user } = useAuth();
  const leitung = LEITUNG.includes(user?.role);
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [scan, setScan] = useState(false);
  const laden = useCallback(async () => {
    try { setD((await ernaehrungAPI.stationEingang()).data); setFehler(null); }
    catch (e) { setFehler(fehlertext(e, 'Eingang konnte nicht geladen werden')); }
  }, []);
  const onCode = useCallback(async (code) => {
    setScan(false);
    try {
      const r = await ernaehrungAPI.stationCode(code);
      toast.success(r.data.doppelt ? 'Diese Messung ist schon im Eingang' : 'Messung im Eingang – jetzt Mitglied wählen');
      laden();
    } catch (e) { toast.error(fehlertext(e, 'Dieser Code lässt sich nicht lesen')); }
  }, [laden]);
  const automatik = async (aktiv) => {
    try { await ernaehrungAPI.stationAutomatik(aktiv); toast.success(aktiv ? 'Automatik an' : 'Automatik aus'); laden(); }
    catch (e) { toast.error(fehlertext(e)); }
  };
  const zurueck = async (id) => {
    try { await ernaehrungAPI.stationRueckgaengig(id); toast.success('Zurückgenommen – die Messung steht wieder offen'); laden(); }
    catch (e) { toast.error(fehlertext(e)); }
  };
  useEffect(() => {
    laden();
    const t = setInterval(laden, 15000);            // neue Messungen erscheinen ohne Neuladen
    return () => clearInterval(t);
  }, [laden]);

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">Waage</h1>
          <p className="text-sm text-muted-foreground">Gastmessungen der InBody 270: „Waage scannen“ (QR-Code am Display) oder automatisch über die Station – dann per Tipp dem Mitglied geben. Nicht zugeordnete Messungen sind nach 24 Stunden gelöscht.</p>
        </div>
        <div className="flex gap-2">
          <Button type="button" size="sm" onClick={() => setScan(true)}><Camera className="w-4 h-4" /> Waage scannen</Button>
          <Button type="button" variant="outline" size="sm" onClick={laden}><RefreshCw className="w-4 h-4" /> Aktualisieren</Button>
        </div>
      </div>
      {scan && (
        <Card><CardContent className="pt-4 space-y-2">
          <p className="text-sm text-muted-foreground">Den QR-Code auf dem Display der Waage in die Kamera halten (direkt nach der Messung).</p>
          <Scanner onCode={onCode} onAbbruch={() => setScan(false)} />
        </CardContent></Card>
      )}
      {fehler && <p className="text-sm text-red-700">{fehler}</p>}
      {d && (
        <>
          {!d.aktiv && <p className="text-sm text-amber-700">Die Körperanalyse ist ausgeschaltet (Ernährung › Einstellungen). Messungen kommen an, lassen sich aber erst nach dem Einschalten zuordnen.</p>}
          <section aria-label="Station" className="space-y-1">
            {d.stationen.length ? d.stationen.map((s) => <Station key={s.station_id} s={s} />)
              : <p className="text-sm text-muted-foreground">Noch keine Station gemeldet.</p>}
          </section>
          <Card><CardContent className="pt-4 flex flex-wrap items-center gap-3">
            <Wand2 className="w-4 h-4" aria-hidden="true" />
            <div className="text-sm mr-auto">
              <p className="font-medium">Automatisch zuordnen: {d.automatik ? 'an' : 'aus'}</p>
              <p className="text-xs text-muted-foreground">Nur wenn genau eine eingecheckte Person (oder ihr Check-Up) passt – Größe und Alter an der Waage stimmen mit ihrem Profil – und ihre Einwilligung schon vorliegt. Sonst bleibt die Messung hier. 24 Stunden rückgängig.</p>
            </div>
            {leitung && <Button type="button" size="sm" variant={d.automatik ? 'outline' : 'default'} onClick={() => automatik(!d.automatik)}>{d.automatik ? 'Ausschalten' : 'Einschalten'}</Button>}
          </CardContent></Card>
          <p className="text-xs text-muted-foreground">{d.hinweis}</p>
          {d.offen.length === 0 && <p className="text-sm text-muted-foreground">Keine offenen Messungen.</p>}
          {d.offen.map((m) => <Messung key={m.id} m={m} onFertig={laden} />)}
          {d.erledigt.length > 0 && (
            <section aria-label="Zuletzt erledigt" className="space-y-1">
              <h2 className="text-sm font-medium">Zuletzt erledigt</h2>
              <ul className="text-sm text-muted-foreground space-y-0.5">
                {d.erledigt.map((e) => (
                  <li key={e.id} className="flex flex-wrap items-center gap-2">
                    <span>{zeit(e.gemessen_am)} · {STATUS[e.status] || e.status}{e.name ? ` · ${e.name}` : ''}{e.zugeordnet_von_name ? ` (${e.zugeordnet_von_name})` : ''}</span>
                    {e.rueckgaengig_moeglich && <Button type="button" size="sm" variant="ghost" className="h-7" onClick={() => zurueck(e.id)}><Undo2 className="w-3.5 h-3.5" /> Falsche Person? Rückgängig</Button>}
                  </li>
                ))}
              </ul>
            </section>
          )}
          {leitung && <Export />}
        </>
      )}
    </div>
  );
}
